import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext.js";
import { LoginPage } from "./pages/LoginPage.js";
import { SignUpPage } from "./pages/SignUpPage.js";
import { VerifyEmailPage } from "./pages/VerifyEmailPage.js";
import { ResendVerificationPage } from "./pages/ResendVerificationPage.js";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage.js";
import { ResetPasswordPage } from "./pages/ResetPasswordPage.js";
import { AccountPage } from "./pages/AccountPage.js";
import { GeneratePage } from "./pages/GeneratePage.js";
import { AdminPage } from "./pages/AdminPage.js";
import { AdminWorkspaceHistoryPage } from "./pages/AdminWorkspaceHistoryPage.js";
import { CreateWorkspacePage } from "./pages/CreateWorkspacePage.js";
import { WorkspaceSettingsPage } from "./pages/WorkspaceSettingsPage.js";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage.js";
import { BrandLockup } from "./components/BrandLockup.js";

/** Emma OS GENERATE productization, Phase B: the real workspace switcher — replaces the silent `accessibleTenants[0]` fallback. Emma's own management tenant (OWNER-only) is included as a synthetic entry since it never appears in `workspaces` (Section 15: it stays out of normal customer/workspace UX otherwise). */
function WorkspaceSwitcher() {
  const { user, workspaces, videoManagementTenantId, selectedTenantId, selectTenant } = useAuth();
  const options = [
    ...(user?.isPlatformOwner && videoManagementTenantId !== null ? [{ tenantId: videoManagementTenantId, name: "Emma (internal)" }] : []),
    ...workspaces,
  ];
  if (options.length === 0) {
    return (
      <a href="/workspaces/new" className="nav-link">
        + New workspace
      </a>
    );
  }
  return (
    <select
      aria-label="Current workspace"
      value={selectedTenantId ?? ""}
      onChange={(event) => selectTenant(event.target.value)}
      style={{ marginRight: 8 }}
    >
      {options.map((option) => (
        <option key={option.tenantId} value={option.tenantId}>
          {option.name}
        </option>
      ))}
    </select>
  );
}

function TopNav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  async function handleLogout(): Promise<void> {
    await logout();
    navigate("/login", { replace: true });
  }

  // FIX-006 (customer product-surface boundary), corrected: an ordinary
  // customer's top-level nav is GENERATE only, full stop — WORKSPACE
  // (team/member management) and ADMIN (platform management) are NEVER
  // peer nav items for a customer, even one who created and administers
  // their own workspace. A workspace's own `ADMIN` tenant-membership role
  // (no longer given to a new workspace's creator, who starts as OPERATOR - see
  // `CreateWorkspace`) remains a real internal fact used elsewhere for
  // onboarding/ownership, but is deliberately NOT sufficient to see this
  // nav item or reach `/workspace/settings` — see that page and
  // `workspace-routes.ts`'s own matching backend correction. `/workspaces/new`
  // (creating/joining a workspace at all) is a SEPARATE, unrestricted
  // route — the FIX-009 onboarding invariant that every user must be able
  // to create/join a workspace is untouched by this.
  const canSeeWorkspaceSettings = user?.isPlatformOwner === true;

  return (
    <div className="top-nav">
      <div className="brand">
        <BrandLockup size="sm" />
      </div>
      <nav>
        <a href="/generate" className={`nav-link ${location.pathname.startsWith("/generate") ? "active" : ""}`}>
          GENERATE
        </a>
        {canSeeWorkspaceSettings && (
          <a href="/workspace/settings" className={`nav-link ${location.pathname.startsWith("/workspace/settings") ? "active" : ""}`}>
            WORKSPACE
          </a>
        )}
        {user?.isPlatformOwner && (
          <a href="/admin" className={`nav-link ${location.pathname.startsWith("/admin") ? "active" : ""}`}>
            ADMIN
          </a>
        )}
      </nav>
      <div className="spacer" />
      <WorkspaceSwitcher />
      <div className="user-info">
        <a href="/account" className={`nav-link ${location.pathname.startsWith("/account") ? "active" : ""}`}>
          {user?.displayName ?? user?.email}
        </a>
        {user?.isPlatformOwner && <span className="badge status-approved">OWNER</span>}
        <button onClick={() => void handleLogout()}>Log out</button>
      </div>
    </div>
  );
}

function ProtectedShell({ children }: { readonly children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") {
    return <div className="empty-state">Loading…</div>;
  }
  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }
  return (
    <div className="app-shell">
      <TopNav />
      <div className="main-content">{children}</div>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/resend-verification" element={<ResendVerificationPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route
        path="/account"
        element={
          <ProtectedShell>
            <AccountPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/workspaces/new"
        element={
          <ProtectedShell>
            <CreateWorkspacePage />
          </ProtectedShell>
        }
      />
      <Route
        path="/workspace/settings"
        element={
          <ProtectedShell>
            <WorkspaceSettingsPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/generate/*"
        element={
          <ProtectedShell>
            <GeneratePage />
          </ProtectedShell>
        }
      />
      <Route
        path="/admin/workspaces/:tenantId/history/*"
        element={
          <ProtectedShell>
            <AdminWorkspaceHistoryPage />
          </ProtectedShell>
        }
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedShell>
            <AdminPage />
          </ProtectedShell>
        }
      />
      <Route path="*" element={<Navigate to="/generate" replace />} />
    </Routes>
  );
}
